// =============================================================================
// GiftCardLoadAction — "Activeren" / "Waarde toevoegen" for a physical gift
// card: pick an amount and the location, then charge it on that location's
// WisePOS through TerminalChargePanel (driver: booking-giftcard). The card is
// loaded server-side once Stripe reports the payment succeeded; this UI only
// reflects it.
// =============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocationFilter } from '../lib/location-context';
import { formatEuro, type GiftCard } from '../lib/giftcard';
import { giftCardLoadDriver } from '../api/giftcard';
import {
  readerProblemText,
  readerUnusable,
  terminalReaderStatus,
  type TerminalReaderState,
} from '../api/terminal';
import { TerminalChargePanel, type TerminalChargeHandle } from './TerminalChargePanel';

const PRESETS = [2500, 5000, 7500, 10000];
const MIN_CENTS = 500;
const MAX_CENTS = 25000;
const MAX_BALANCE_CENTS = 50000;

export function GiftCardLoadAction({ card, onSettled }: { card: GiftCard; onSettled: () => void }) {
  const [open, setOpen] = useState(false);
  const activation = card.status === 'inactive';
  return (
    <>
      <button className="walkin-primary-btn" onClick={() => setOpen(true)}>
        {activation ? 'Activeren' : 'Waarde toevoegen'}
      </button>
      {open ? (
        <GiftCardLoadModal
          card={card}
          onClose={(changed) => {
            setOpen(false);
            if (changed) onSettled();
          }}
        />
      ) : null}
    </>
  );
}

function GiftCardLoadModal({ card, onClose }: { card: GiftCard; onClose: (changed: boolean) => void }) {
  const { locationId: ctxLocationId, locations } = useLocationFilter();
  const activation = card.status === 'inactive';
  const [preset, setPreset] = useState<number | null>(5000);
  const [custom, setCustom] = useState('');
  const [locationId, setLocationId] = useState<string | null>(
    ctxLocationId ?? (locations.length === 1 ? locations[0]?.id ?? null : null),
  );
  const [reader, setReader] = useState<TerminalReaderState | null>(null);
  const [charging, setCharging] = useState(false);
  const [settled, setSettled] = useState(false);
  const panel = useRef<TerminalChargeHandle>(null);

  const customCents = custom.trim() ? Math.round(Number(custom.replace(',', '.')) * 100) : null;
  const amountCents = customCents ?? preset;
  const room = MAX_BALANCE_CENTS - card.balance_cents;
  const amountValid =
    amountCents != null && Number.isInteger(amountCents) &&
    amountCents >= MIN_CENTS && amountCents <= MAX_CENTS && amountCents <= room;

  useEffect(() => {
    if (!locationId) {
      setReader(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await terminalReaderStatus(locationId);
      if (!cancelled) setReader(res.ok ? res.data : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  // One driver per charge: created when the operator presses Afrekenen.
  const driver = useMemo(
    () => (charging && amountCents && locationId ? giftCardLoadDriver(card.code, amountCents, locationId) : null),
    // amount/location are frozen while charging
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [charging],
  );

  const close = useCallback(async () => {
    if (charging && !settled) await panel.current?.clearReader();
    onClose(settled);
  }, [charging, settled, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') void close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const noReader = reader != null && !reader.configured;
  const problem = noReader ? 'Geen pinterminal ingesteld voor deze locatie (Setup → Locaties).' : readerProblemText(reader);
  const canCharge = amountValid && !!locationId && !noReader && !readerUnusable(reader);

  return (
    <div className="dep-modal-backdrop" onClick={() => { /* ✕ / Esc only */ }}>
      <div className="dep-modal cancel-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dep-modal-head">
          <div>
            <h2>{activation ? 'Cadeaubon activeren' : 'Waarde toevoegen'}</h2>
            <div className="dep-modal-sub muted">
              {card.code_display}
              {!activation ? ` · saldo ${formatEuro(card.balance_cents)}` : ''}
            </div>
          </div>
          <button className="btn-link dep-modal-close" onClick={() => void close()} aria-label="Sluiten">✕</button>
        </header>
        <div className="dep-modal-body">
          {!charging ? (
            <div className="walkin-step">
              <div className="giftcard-presets" role="group" aria-label="Bedrag">
                {PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`giftcard-preset${preset === c && !custom.trim() ? ' is-selected' : ''}`}
                    disabled={c > room}
                    onClick={() => {
                      setPreset(c);
                      setCustom('');
                    }}
                  >
                    {formatEuro(c)}
                  </button>
                ))}
              </div>
              <label className="walkin-field">
                <span>Ander bedrag (€ 5 – € 250)</span>
                <input
                  inputMode="decimal"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="bijv. 40"
                />
              </label>
              {amountCents != null && amountCents > room ? (
                <div className="error-card">Er mag maximaal {formatEuro(MAX_BALANCE_CENTS)} op een kaart staan.</div>
              ) : null}
              {!ctxLocationId && locations.length > 1 ? (
                <label className="walkin-field">
                  <span>Locatie (pinterminal)</span>
                  <select value={locationId ?? ''} onChange={(e) => setLocationId(e.target.value || null)}>
                    <option value="">Kies locatie…</option>
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>{l.display_name ?? l.name}</option>
                    ))}
                  </select>
                </label>
              ) : null}
              {problem ? <div className="error-card">{problem}</div> : null}
              <p className="muted">
                {activation
                  ? 'De kaart wordt actief zodra de betaling gelukt is en is daarna 2 jaar geldig.'
                  : 'Het bedrag komt op de kaart zodra de betaling gelukt is; de kaart is daarna weer 2 jaar geldig.'}
              </p>
              <button
                className="walkin-primary-btn"
                disabled={!canCharge}
                onClick={() => setCharging(true)}
              >
                Afrekenen {amountValid && amountCents ? formatEuro(amountCents) : ''}
              </button>
            </div>
          ) : driver ? (
            <>
              <TerminalChargePanel
                ref={panel}
                driver={driver}
                hideAbandon
                onSettled={() => setSettled(true)}
                onAbandon={() => onClose(false)}
              />
              {settled ? (
                <div className="walkin-step">
                  <div className="walkin-success-title">
                    ✓ {activation ? 'Kaart geactiveerd' : 'Waarde toegevoegd'}
                  </div>
                  <button className="walkin-primary-btn" onClick={() => onClose(true)}>Klaar</button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
